"use client";

import { useEffect, useState } from "react";

const SAVED_KEY = "pbd_saved";

function readSaved(): string[] {
  try {
    return JSON.parse(localStorage.getItem(SAVED_KEY) ?? "[]");
  } catch {
    return [];
  }
}

/** Working Save (local bookmark) + Share toolbar, the editorial article actions. */
export function SaveShare({ id, title, light = false }: { id: string; title: string; light?: boolean }) {
  const [saved, setSaved] = useState(false);
  const [shared, setShared] = useState(false);

  useEffect(() => {
    // Hydrate from localStorage after mount (not available in SSR).
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSaved(readSaved().includes(id));
  }, [id]);

  const toggleSave = () => {
    const list = readSaved();
    const next = list.includes(id) ? list.filter((s) => s !== id) : [...list, id];
    try {
      localStorage.setItem(SAVED_KEY, JSON.stringify(next));
    } catch {
      // ignore storage failures
    }
    setSaved(next.includes(id));
  };

  const share = async () => {
    const url = typeof window !== "undefined" ? window.location.href : "";
    try {
      if (navigator.share) await navigator.share({ title, url });
      else {
        await navigator.clipboard.writeText(url);
        setShared(true);
        window.setTimeout(() => setShared(false), 1800);
      }
    } catch {
      // cancelled / unsupported
    }
  };

  const idle = light ? "rgba(255,255,255,0.85)" : "var(--foreground)";
  const border = light ? "rgba(255,255,255,0.35)" : "var(--border-subtle)";
  // On the `light` (photo-overlay) variant --accent's navy would be
  // near-invisible against the dark scrim, so the saved state there needs
  // the always-vivid --cta color instead.
  const savedColor = light ? "var(--cta)" : "var(--accent)";
  const base =
    "inline-flex items-center gap-2 rounded-full border px-4 py-2 text-[13px] font-semibold no-underline cursor-pointer transition-colors duration-150 font-[inherit]";

  return (
    <div className="flex items-center gap-2.5">
      <button
        type="button"
        onClick={toggleSave}
        aria-pressed={saved}
        className={base}
        style={{
          borderColor: saved ? savedColor : border,
          background: saved ? `color-mix(in srgb, ${savedColor} 16%, transparent)` : "transparent",
          color: saved ? savedColor : idle,
        }}
      >
        <svg width="15" height="15" viewBox="0 0 24 24" fill={saved ? "currentColor" : "none"} aria-hidden>
          <path d="M6 4h12v17l-6-4-6 4V4z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
        </svg>
        {saved ? "Saved" : "Save"}
      </button>
      <button
        type="button"
        onClick={share}
        className={base}
        style={{ borderColor: border, background: "transparent", color: idle }}
      >
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden>
          <path d="M4 12v7a1 1 0 001 1h14a1 1 0 001-1v-7M16 6l-4-4-4 4M12 2v13" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        {shared ? "Link copied" : "Share"}
      </button>
    </div>
  );
}
